/**
 * An uninsured flown fleet ship's total loss on death (M6 spec §4 "an uninsured loss is total"; D-394 gap 2; plan
 * P24b). `death.ts applyDeath` calls `fallBackToLeaseShip` after its own changes (the S4 → SY-2b → P21 order stays
 * as it was; this step only runs for a lost fleet ship):
 *
 * - **Which deaths:** the player flies a fleet ship (`career.flownShip`) that is not insured (`flownShipInsured`,
 *   which never counts before ownership) and the Career's own lease ship is parked in the hangar (`OWN_SHIP_ID`).
 *   An insured fleet ship is rebuilt with the M2 fee rule as before; a death in the lease ship is unchanged.
 * - **The Career side:** nothing is rebuilt, so no rebuild fee (the repo fee still applies). The lost ship's hangar
 *   entry is dropped; the lease ship's entry becomes `career.ship` again (its loadout, salvaged provenance, stored
 *   hull and torpedoes; absent = full) and leaves the hangar; `flownShip` goes. The hold went into the death pod.
 *   The pilot respawns at the lease ship's station (`dockedAt`) when the runtime knows it, else by the M2 rule.
 * - **The worker side:** the runtime queues the lost id (`noteBoardedShipLost`); the fleet link's frame sends the
 *   worker's `loseBoardedShip`, which frees the boarded slot as a loss.
 *
 * Pure except the small loss queue (a rare event; it may allocate).
 */
import type { M2Data } from '../data/m2Schemas'
import { holdCapacityT } from '../career/hullFit'
import type { Career, ShipState } from '../career/types'
import { flownShipInsured } from './insurance'
import { OWN_SHIP_ID, loadoutOf, ownedShip } from './hangar'
import type { ShipLimitsOf } from './switch'

/** The fleet id a death now would lose outright (an uninsured flown fleet ship with the lease ship parked), or null. */
export function totalLossOf(career: Career): string | null {
  const id = career.flownShip
  if (id === undefined || id === OWN_SHIP_ID) return null
  if (ownedShip(career, id) === null || ownedShip(career, OWN_SHIP_ID) === null) return null
  return flownShipInsured(career) ? null : id
}

/**
 * After a total loss of fleet ship `lost`: the pilot is back in the parked lease ship. `stationKnown` says whether
 * the runtime can respawn at the lease ship's `dockedAt` station (default: never, keeping `lastDockedStation`).
 */
export function fallBackToLeaseShip(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  lost: string,
  limitsOf: ShipLimitsOf,
  stationKnown: (stationId: string) => boolean = () => false,
): Career {
  const own = ownedShip(career, OWN_SHIP_ID)
  if (own === null) return career
  const loadout = loadoutOf(own)
  const full = limitsOf(own.defId)
  const ship: ShipState = {
    defId: loadout.defId,
    hull: own.hull ?? full?.hull ?? career.ship.hull,
    torpedoes: own.torpedoes ?? full?.torpedoRounds ?? 0,
    fitted: loadout.fitted,
    locker: loadout.locker,
  }
  if (loadout.salvaged !== undefined) ship.salvaged = loadout.salvaged
  if (loadout.resonanceMeter === true) ship.resonanceMeter = true
  // M10 P5a6a: the lease ship's hull-rated hold (`career/hullFit.ts holdCapacityT`; the rack's own with no `fit`).
  const capacityT = holdCapacityT(loadout.defId, loadout.fitted, data)
  // M10 P5a11b: an S riding in the lost L's cradle goes down with its hull (`lostCarriedOf`; the worker frees it too).
  const carried = lostCarriedOf(career, lost)
  const hangar = (career.hangar ?? []).filter((s) => s.fleetId !== lost && s.fleetId !== OWN_SHIP_ID && s.fleetId !== carried)
  const next: Career = { ...career, ship, cargo: { ...career.cargo, capacityT }, hangar }
  delete next.flownShip
  if (own.dockedAt !== undefined && stationKnown(own.dockedAt)) next.lastDockedStation = own.dockedAt
  return next
}

/**
 * M10 P5a11b: the fleet S riding in the cradle of fleet ship `lost` (its hangar entry's `cradle.carried`), lost with
 * it when the flown L is lost outright, or null. The worker frees its `CARRIED_UNTIL` slot through the same
 * `loseBoardedShip` (which pays the S's own claim when insured, as any owned ship).
 */
export function lostCarriedOf(career: Career, lost: string): string | null {
  const carried = ownedShip(career, lost)?.cradle?.carried ?? null
  return carried !== null && carried !== OWN_SHIP_ID && ownedShip(career, carried) !== null ? carried : null
}

/**
 * M10 P5a11b (plan P5a11b; spec §6.5): the parked L `fleetId` was destroyed in space while the pilot flies another
 * ship. Its hangar entry goes (its own hold with it) and `parkedL` goes; if the flown S rode on its pad
 * (`carriedBy`), the S launches free (the cradle runtime's dead-carrier release, D-1086 C3) and `carriedBy` goes.
 * Insurance is as for any owned ship: the worker pays the claim when it frees the L's `CARRIED_UNTIL` slot
 * (`noteBoardedShipLost` → `loseBoardedShip`). Unchanged when `fleetId` is not the parked or carrying L, or is
 * the flown ship (that is a death, `death.ts`). Pure.
 */
export function loseParkedL(career: Career, fleetId: string): Career {
  const parked = career.parkedL?.fleetId === fleetId
  const carrying = career.carriedBy === fleetId
  if ((!parked && !carrying) || career.flownShip === fleetId) return career
  const next: Career = { ...career, hangar: (career.hangar ?? []).filter((s) => s.fleetId !== fleetId) }
  if (parked) delete next.parkedL
  if (carrying) delete next.carriedBy
  return next
}

/** Lost boarded fleet ships the worker has not been told about yet (oldest first; bounded). */
const pendingLosses: string[] = []
const MAX_PENDING = 16

/** The runtime lost the flown fleet ship `fleetId`: the fleet link tells the worker (`loseBoardedShip`). */
export function noteBoardedShipLost(fleetId: string): void {
  if (pendingLosses.length >= MAX_PENDING) pendingLosses.shift()
  pendingLosses.push(fleetId)
}

/** How many losses wait for the worker (the fleet link's frame drains them when non-zero). */
export function pendingBoardedLosses(): number {
  return pendingLosses.length
}

/** Takes every waiting loss, oldest first. */
export function takeBoardedLosses(): string[] {
  return pendingLosses.splice(0, pendingLosses.length)
}
